import BaseModel2d from './BaseModel2d';
import { globalStores } from '@/editor/stores';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { ENTITY_RENDER_ORDER, hoverOutlineColor, selectedOutlineColor } from '@/editor/constant/entity';
import { pixelRatio2d } from '@/editor/math/const';
import { pathToGmtShape } from '@/editor/utils/shape';
import { Group, Mesh, MeshBasicMaterial, ShapeGeometry } from 'three';
import { Line2, LineGeometry, LineMaterial } from 'three/examples/jsm/Addons.js';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { disposeAllChildren, disposeAllChildrenThenRemoveFromParent } from '../utils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';

const AREA_DIMENSION_COLOR = selectedOutlineColor;
const AREA_DIMENSION_COLOR_HOVERED = hoverOutlineColor;
const AREA_DIMENSION_COLOR_SELECTED = selectedOutlineColor;
const AREA_DIMENSION_FILL_OPACITY = 0.15;
const AREA_DIMENSION_FILL_OPACITY_HOVERED = 0.15;
const AREA_DIMENSION_FILL_OPACITY_SELECTED = 0.25;

export interface IAreaDimensionModelData {
  id: string;
  points: IPoint2d[];
}

export interface IAreaDimensionModelOptions {
  closed?: boolean;
  fill?: boolean;
  checkValid?: boolean;
}

export class AreaDimensionModel extends BaseModel2d {
  private _renderOrder = ENTITY_RENDER_ORDER.dimension;
  private _fillMaterial: MeshBasicMaterial | null = null;
  private _outlineMaterial: LineMaterial | null = null;
  private _hover = false;
  private _selected = false;
  private _maskGroup = new Group();

  constructor(areaDimension: IAreaDimensionModelData) {
    super();

    this._modelGroup.userData.containerType = EContainerType.noIntersectable;
    this._modelGroup.userData.entityId = areaDimension.id;
    this._modelGroup.userData.type = EEntityType.AreaDimension;

    this.setDisplayOrder(this._renderOrder);
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._maskGroup);
    this.updateModel(areaDimension);
  }

  updateModel(areaDimension: IAreaDimensionModelData, options: IAreaDimensionModelOptions = {}) {
    let closed = options.closed ?? true;
    let fill = options.fill ?? true;
    let validPoints: IPoint2d[] = closed ? [...areaDimension.points, areaDimension.points[0]] : areaDimension.points;
    let invalidPoints: IPoint2d[] = [];

    if (options.checkValid) {
      const { validPoints: shadowValidPoints, invalidPoints: shadowInvalidPoints } = splitPointsBySegmentValidity([
        ...validPoints
      ]);
      validPoints = shadowValidPoints;
      invalidPoints = shadowInvalidPoints;
      if (invalidPoints.length) {
        closed = false;
        fill = false;
      }
    }

    this.clear();
    this._fillMaterial = null;
    this._modelGroup.userData.entityId = areaDimension.id;

    if (!areaDimension.points.length) return;

    if (closed && fill && areaDimension.points.length >= 3) {
      this.clearMask();
      this._createFill(areaDimension);
    }
    if (areaDimension.points.length >= 2) {
      this._createOutline(validPoints, closed && areaDimension.points.length >= 3);
      if (invalidPoints.length) this._createIllegalOutline(invalidPoints);
    }
    this._syncState();
  }

  setHover(hover: boolean) {
    if (this._hover === hover) return;

    this._hover = hover;
    this._syncState();
  }

  setSelected(selected: boolean) {
    if (this._selected === selected) return;

    this._selected = selected;
    this._syncState();
  }

  private _createFill(areaDimension: IAreaDimensionModelData) {
    const geometry = new ShapeGeometry(pathToGmtShape(areaDimension.points));
    const material = new MeshBasicMaterial({
      color: AREA_DIMENSION_COLOR,
      transparent: true,
      opacity: AREA_DIMENSION_FILL_OPACITY,
      depthTest: false,
      depthWrite: false
    });
    this._fillMaterial = material;
    const mesh = new Mesh(geometry, material);
    mesh.renderOrder = this._renderOrder;

    this._modelGroup.add(mesh);
  }

  private _createOutline(points: IPoint2d[], closed: boolean) {
    const linePoints = closed ? [...points, points[0]] : points;

    const geometry = new LineGeometry();
    geometry.setPositions(linePoints.flatMap((point) => [point.x / pixelRatio2d, point.y / pixelRatio2d, 0]));
    this._outlineMaterial = new LineMaterial({
      color: AREA_DIMENSION_COLOR,
      linewidth: 1,
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false
    });
    const line = new Line2(geometry, this._outlineMaterial);
    line.renderOrder = this._renderOrder + 1;

    this._modelGroup.add(line);
  }

  private _createIllegalOutline(points: IPoint2d[]) {
    const geometry = new LineGeometry();
    geometry.setPositions(points.flatMap((point) => [point.x / pixelRatio2d, point.y / pixelRatio2d, 0]));
    this._outlineMaterial = new LineMaterial({
      color: 0xdf4545,
      linewidth: 1,
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false
    });
    const line = new Line2(geometry, this._outlineMaterial);
    line.renderOrder = this._renderOrder + 1;

    this._modelGroup.add(line);
  }

  private _syncState() {
    if (!this._fillMaterial || !this._outlineMaterial) return;
    if (!this._hover && !this._selected) {
      this._fillMaterial.color.set(AREA_DIMENSION_COLOR);
      this._outlineMaterial.color.set(AREA_DIMENSION_COLOR);
      this._fillMaterial.opacity = AREA_DIMENSION_FILL_OPACITY;
    } else if (!this._selected && this._hover) {
      this._fillMaterial.color.set(AREA_DIMENSION_COLOR_HOVERED);
      this._outlineMaterial.color.set(AREA_DIMENSION_COLOR_HOVERED);
      this._fillMaterial.opacity = AREA_DIMENSION_FILL_OPACITY_HOVERED;
    } else if (this._selected) {
      this._fillMaterial.color.set(AREA_DIMENSION_COLOR_SELECTED);
      this._outlineMaterial.color.set(AREA_DIMENSION_COLOR_SELECTED);
      this._fillMaterial.opacity = AREA_DIMENSION_FILL_OPACITY_SELECTED;
    }
  }

  updateMask(points: IPoint2d[]) {
    const geometry = new ShapeGeometry(pathToGmtShape(points));
    const material = new MeshBasicMaterial({
      color: AREA_DIMENSION_COLOR,
      transparent: true,
      opacity: AREA_DIMENSION_FILL_OPACITY,
      depthTest: false,
      depthWrite: false
    });
    this._fillMaterial = material;
    const mesh = new Mesh(geometry, material);
    mesh.renderOrder = this._renderOrder;

    this.clearMask();
    this._maskGroup.add(mesh);
  }

  clearMask() {
    disposeAllChildren(this._maskGroup);
  }

  /** 覆写：高亮遮罩是独立挂在场景上的第二个 group，隐藏时要一起带上 */
  override setVisible(visible: boolean) {
    super.setVisible(visible);
    this._maskGroup.visible = visible;
  }

  override dispose() {
    disposeAllChildrenThenRemoveFromParent(this._maskGroup);
    disposeAllChildrenThenRemoveFromParent(this._modelGroup);
  }
}

/**
 * 找出相交线段，并返回合法/不合法线段所涉及的点
 */
function splitPointsBySegmentValidity(points: IPoint2d[]) {
  if (points.length < 4) {
    return { validPoints: [...points], invalidPoints: [] };
  }

  const segments: { seg: LineSeg2d; index: number }[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    segments.push({ seg: new LineSeg2d(points[i], points[i + 1]), index: i });
  }

  const invalidSegmentIdx = new Set<number>();

  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      // 相邻线段共享端点，不视为相交
      if (j === i + 1) continue;

      if (segments[i].seg.intersection(segments[j].seg)) {
        invalidSegmentIdx.add(j); // 只标记“后出现”的线段为不合法
      }
    }
  }

  const validSegmentIdx = segments.filter((seg) => !invalidSegmentIdx.has(seg.index)).map((seg) => seg.index);

  const invalidSegmentIdxArr = Array.from(invalidSegmentIdx.values());

  return {
    validPoints: collectPoints(points, validSegmentIdx),
    invalidPoints: collectPoints(points, invalidSegmentIdxArr)
  };
}

function collectPoints(points: IPoint2d[], segmentIndexes: number[]): IPoint2d[] {
  if (segmentIndexes.length === 0) return [];

  const pointFlags = new Array(points.length).fill(false);
  for (const segIdx of segmentIndexes) {
    pointFlags[segIdx] = true; // segment start
    pointFlags[segIdx + 1] = true; // segment end
  }

  const result: IPoint2d[] = [];
  for (let i = 0; i < points.length; i++) {
    if (pointFlags[i]) result.push(points[i]);
  }
  return result;
}
